import { resolveVatCode } from "services/vatService";

export function transformChitsForBatch(
  data,
  { suppliersdata, branchesList, allVats, defaultsByChit } = {},
) {
  return data.map((item) => {
    const supplierName = suppliersdata?.find(
      (s) => s.supplierRef === item.supplierRef,
    )?.supplierName;
    const branchName = branchesList?.find(
      (b) => b.branchId === item.branchId,
    )?.branchName;
    return {
      ...item,
      date: new Date(item.date),
      matterID:
        typeof item.matterID !== "object"
          ? {
            matterRef: item.matterRef,
            matterId: item.matterID,
          }
          : item.matterID,
      supplierRef:
        item.supplierRef && typeof item.supplierRef !== "object"
          ? {
            supplierRef: item.supplierRef,
            display: supplierName
              ? `${item.supplierRef} - ${supplierName}`
              : item.supplierRef,
          }
          : item.supplierRef,
      nominalRef:
        item.nominalRef && typeof item.nominalRef !== "object"
          ? {
            nominalRef: item.nominalRef,
            display: `${item.nominalInfo?.nominalRef} - ${item.nominalInfo?.nominalName}`,
          }
          : item.nominalRef,
      ddaBank:
        item.ddaBank && typeof item.ddaBank === "object"
          ? {
            ...item.ddaBank,
            display: `${item.ddaBank?.nominalRef} - ${item.ddaBank?.nominalName}`,
          }
          : item.ddaBank,
      officeBank:
        item.officeBank && typeof item.officeBank === "object"
          ? {
            ...item.officeBank,
            display: `${item.officeBank?.nominalRef} - ${item.officeBank?.nominalName}`,
          }
          : item.officeBank,
      clientBank:
        item.clientBank && typeof item.clientBank === "object"
          ? {
            ...item.clientBank,
            display: `${item.clientBank?.nominalRef} - ${item.clientBank?.nominalName}`,
          }
          : item.clientBank,
      feeEarnerRef:
        item.feeEarnerRef && typeof item.feeEarnerRef === "object"
          ? {
            ...item.feeEarnerRef,
            display: `${item.feeEarnerRef?.userRef} - ${item.feeEarnerRef?.displayname}`,
          }
          : item.feeEarnerRef,
      branchId:
        item.branchId && typeof item.branchId !== "object"
          ? {
            branchId: item.branchId,
            display: branchName
              ? `${item.branchId} - ${branchName}`
              : item.branchId,
          }
          : item.branchId,
      vatCode: resolveVatCode(item.vatCode, allVats) ?? item.vatCode,
      ...(defaultsByChit?.[item.chitId] || {}),
    };
  });
}

const makeBankResolver = (nominals) => (nominalRef) => {
  if (!nominalRef) return null;
  const match = nominals?.find((n) => n.nominalRef === nominalRef);
  return {
    nominalRef,
    display: match ? `${match.nominalRef} - ${match.nominalName}` : nominalRef,
  };
};

const isObjectBank = (value) =>
  Boolean(value && typeof value === "object" && value.nominalRef);

const getMatterId = (matterID) =>
  matterID && typeof matterID === "object" ? matterID.matterId : matterID;

export async function resolveBatchBankDefaults(
  data,
  { getMatterDefaults, officeBankNominals, clientBankNominals } = {},
) {
  const defaultsByChit = {};
  if (!getMatterDefaults || !Array.isArray(data)) return defaultsByChit;

  const cache = new Map();
  const fetchDefaults = (matterID) => {
    const matterId = getMatterId(matterID);
    if (!matterId) return Promise.resolve(null);
    if (!cache.has(matterId)) {
      cache.set(
        matterId,
        getMatterDefaults(matterId)
          .then((res) => res?.data?.data ?? null)
          .catch(() => null),
      );
    }
    return cache.get(matterId);
  };

  const resolveClientBank = makeBankResolver(clientBankNominals);
  const resolveOfficeBank = makeBankResolver(officeBankNominals);

  await Promise.all(
    data.map(async (item) => {
      const code = item.postingType?.code;

      if (code === "CTC") {
        const [fromDefaults, toDefaults] = await Promise.all([
          fetchDefaults(item.matterID),
          fetchDefaults(item.matterIDTo),
        ]);
        defaultsByChit[item.chitId] = {
          clientBankFrom: resolveClientBank(fromDefaults?.defaultClientBank),
          clientBankTo: resolveClientBank(toDefaults?.defaultClientBank),
          matterIDTo: item.matterIDTo
            ? { matterId: item.matterIDTo, matterRef: item.matterRefTo }
            : null,
        };
        return;
      }

      if (code === "CTO" || code === "OTC") {
        const officeNeedsDefault = !isObjectBank(item.officeBank);
        const clientNeedsDefault = !isObjectBank(item.clientBank);

        const entry = {};
        let defaults = null;
        if (officeNeedsDefault || clientNeedsDefault) {
          defaults = await fetchDefaults(item.matterID);
        }

        if (officeNeedsDefault && defaults?.defaultOfficeBank) {
          entry.officeBank = resolveOfficeBank(defaults.defaultOfficeBank);
        }
        if (clientNeedsDefault && defaults?.defaultClientBank) {
          entry.clientBank = resolveClientBank(defaults.defaultClientBank);
        }

        if (Object.keys(entry).length > 0) {
          defaultsByChit[item.chitId] = entry;
        }
      }
    }),
  );

  return defaultsByChit;
}

export function applyChitPostingRowTransforms(
  list,
  postingTypes,
  feeearnerdata,
  checkedList,
) {
  if (!list?.data) return;
  list.data.forEach((item) => {
    item.select = false;
    item.postingField = postingTypes?.filter(
      (item1) => item.postingType === item1.code,
    )[0];
    item.postingType = item.postingField;
    if (
      item?.postingField?.code === "DCR" ||
      item?.postingField?.code === "DDR"
    ) {
      item.ddaBank = item.nominalInfo;
    }
    if (
      item?.postingField?.code === "CCR" ||
      item?.postingField?.code === "CDR"
    ) {
      item.clientBank = item.nominalInfo;
    }
    if (
      item?.postingField?.code === "OCR" ||
      item?.postingField?.code === "ODR" ||
      item?.postingField?.code === "DPU"
    ) {
      item.officeBank = item.nominalInfo;
    }

    item.feeEarnerRefOriginal = item.feeEarnerRef;

    item.feeearner = feeearnerdata?.filter(
      (item1) => item.feeEarnerRef === item1.userRef,
    )[0];
    item.feeEarnerRef = item.feeearner;
    checkedList.forEach((item1) => {
      if (item.chitId === item1) {
        item.select = true;
      }
    });
  });
}
